import { Home as HomeIcon, Building2, Sun, LayoutGrid, Award, ShieldCheck, Handshake, PhoneCall, MapPin } from "lucide-react";
import { IMG } from "@/lib/images";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CtaBand } from "@/components/Shared";

const values = [
  { icon: Award, title: "EXPERIENCED PROFESSIONALS", text: "Trained and experienced crews who take pride in their work." },
  { icon: ShieldCheck, title: "QUALITY EQUIPMENT", text: "We use professional equipment and proven cleaning methods." },
  { icon: Handshake, title: "CUSTOMER FOCUSED", text: "We listen, communicate, and tailor our services to your needs." },
];

const stats = [
  { icon: LayoutGrid, value: "70,000+", title: "SOLAR PANELS CLEANED", sub: "+ AND COUNTING" },
  { icon: HomeIcon, value: "RESIDENTIAL", title: "HUNDREDS OF HOMES SERVED" },
  { icon: Building2, value: "COMMERCIAL", title: "BUSINESSES, CHURCHES, SCHOOLS & FACILITIES" },
  { icon: Sun, value: "UTILITY SCALE", title: "LARGE SOLAR FARMS ACROSS TEXAS" },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About Us"
        title={<>Dedicated to Clean Solar.<br />Committed to Results.</>}
      >
        <div className="mt-5 max-w-md space-y-4 text-[15px] leading-relaxed text-[#5B6B7F]">
          <p>Solar Pros Cleaning Services was founded on one simple belief: clean panels perform better. Our mission is to help homeowners, businesses, and solar farm operators get the maximum energy and return on their solar investment through professional, reliable cleaning services.</p>
          <p>From small residential systems to utility-scale solar farms, we bring the same level of care, experience, and attention to every project.</p>
        </div>
      </PageHero>

      {/* OUR STORY */}
      <section className="mx-auto max-w-[1200px] px-5 lg:px-8 py-20">
        <div className="grid gap-12 lg:grid-cols-2">
          <Reveal>
            <h2 className="font-display text-3xl font-extrabold text-[#0A2342]">OUR STORY</h2>
            <span className="mt-4 block h-1 w-14 rounded-full bg-[#1E5EDB]" />
            <div className="mt-6 space-y-4 text-[15px] leading-relaxed text-[#5B6B7F]">
              <p>We started Solar Pros Cleaning Services to provide a better kind of solar panel cleaning service—one built on honesty, hard work, and dependable communication.</p>
              <p>Over the years, we've grown through word of mouth, repeat customers, and a commitment to doing the job right the first time.</p>
              <p>Today, we are proud to serve residential, commercial, and utility-scale solar installations across Texas.</p>
            </div>
          </Reveal>
          <div className="grid gap-8 sm:grid-cols-3">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08} className={`text-center ${i !== 0 ? "sm:border-l sm:border-[#E4EBF3] sm:pl-6" : ""}`}>
                <v.icon size={40} className="mx-auto text-[#1E5EDB]" strokeWidth={1.6} />
                <h3 className="mt-4 font-display text-[14px] font-extrabold leading-tight text-[#0A2342]">{v.title}</h3>
                <p className="mt-2 text-[13px] leading-relaxed text-[#5B6B7F]">{v.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BY THE NUMBERS */}
      <section className="mx-auto max-w-[1200px] px-5 lg:px-8">
        <Reveal>
          <div className="rounded-2xl bg-[#F4F7FB] px-6 py-12">
            <h2 className="text-center font-display text-2xl font-extrabold text-[#1E5EDB]">BY THE NUMBERS</h2>
            <span className="mx-auto mt-3 block h-1 w-12 rounded-full bg-[#1E5EDB]" />
            <div className="mt-10 grid grid-cols-2 gap-y-8 md:grid-cols-4">
              {stats.map((s, i) => (
                <div key={s.title} className={`flex flex-col items-center px-3 text-center ${i !== 0 ? "md:border-l md:border-[#D9E2EE]" : ""}`}>
                  <s.icon size={38} className="text-[#1E5EDB]" strokeWidth={1.7} />
                  <p className="mt-3 font-display text-xl font-extrabold text-[#0A2342]">{s.value}</p>
                  <p className="mt-1 text-[12px] font-semibold uppercase leading-tight tracking-wide text-[#5B6B7F]">{s.title}</p>
                  {s.sub && <p className="text-[11px] font-medium text-[#8A99AB]">{s.sub}</p>}
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* TEXAS PROUD */}
      <section className="mx-auto max-w-[1200px] px-5 lg:px-8 py-20">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <Reveal>
            <div className="overflow-hidden rounded-2xl shadow-[0_20px_50px_-24px_rgba(10,35,66,0.4)]">
              <img src={IMG.truck} alt="Solar Pros truck and equipment" className="h-[300px] w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display text-3xl font-extrabold text-[#1E5EDB]">LOCAL. RELIABLE. TEXAS PROUD.</h2>
            <p className="mt-5 text-[16px] leading-relaxed text-[#5B6B7F]">
              We are a Texas-based company that understands the unique challenges of our climate and dust conditions. We are proud to support clean energy and help our communities get the most from their solar systems.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-[#1E5EDB]/30">
                <MapPin size={20} className="text-[#1E5EDB]" />
              </span>
              <span className="font-display font-bold text-[#0A2342]">Proudly serving communities across Texas.</span>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand
        icon={PhoneCall}
        title="Ready to Experience the Difference?"
        text="Let Solar Pros Cleaning Services help you protect your investment and maximize your energy production."
      />
    </div>
  );
}
